import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createFamilyMember,
  updateFamilyMember,
} from "../../lib/api-modules/family-members.js";
import type {
  CreateFamilyMemberInput,
  FamilyMember,
  FamilyRelationship,
  UpdateFamilyMemberInput,
} from "../../lib/api-modules/types.js";
import { ErrorMessage } from "../auth/error-message.js";

/**
 * <FamilyMemberForm> — create/edit a family member (Phase 5 Session 1).
 *
 * Required: name, relationship. Default relationship = "child" (highest-
 * fill case for beneficiary linkage on 529/UTMA accounts). Optional:
 * date_of_birth, email (for non-user members like grandparents),
 * member_user_id (link to a registered Hearth user), notes.
 */

const RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: FamilyRelationship;
  label: string;
}> = [
  { value: "child", label: "Child" },
  { value: "spouse", label: "Spouse" },
  { value: "self", label: "Self" },
  { value: "parent", label: "Parent" },
  { value: "sibling", label: "Sibling" },
  { value: "grandparent", label: "Grandparent" },
  { value: "grandchild", label: "Grandchild" },
  { value: "other", label: "Other" },
];

interface FamilyMemberFormProps {
  readonly mode: "create" | "edit";
  readonly member?: FamilyMember;
  readonly onClose: () => void;
}

export function FamilyMemberForm({
  mode,
  member,
  onClose,
}: FamilyMemberFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";

  const [name, setName] = useState<string>(member?.name ?? "");
  const [relationship, setRelationship] = useState<FamilyRelationship>(
    member?.relationship ?? "child",
  );
  const [dob, setDob] = useState<string>(member?.date_of_birth ?? "");
  const [email, setEmail] = useState<string>(member?.email ?? "");
  const [memberUserId, setMemberUserId] = useState<string>(
    member?.member_user_id ?? "",
  );
  const [workAnniversaryDate, setWorkAnniversaryDate] = useState<string>(
    member?.work_anniversary_date ?? "",
  );
  const [notes, setNotes] = useState<string>(member?.notes ?? "");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const trimmedEmail = email.trim();
      const trimmedUserId = memberUserId.trim();
      const trimmedNotes = notes.trim();

      if (isEdit && member) {
        const input: UpdateFamilyMemberInput = {
          name: trimmedName,
          relationship,
        };
        if (dob.length > 0) input.date_of_birth = dob;
        if (trimmedEmail.length > 0) input.email = trimmedEmail;
        if (trimmedUserId.length > 0) input.member_user_id = trimmedUserId;
        if (workAnniversaryDate.length > 0)
          input.work_anniversary_date = workAnniversaryDate;
        if (trimmedNotes.length > 0) input.notes = trimmedNotes;
        return updateFamilyMember(member.member_id, input);
      }
      const input: CreateFamilyMemberInput = {
        name: trimmedName,
        relationship,
      };
      if (dob.length > 0) input.date_of_birth = dob;
      if (trimmedEmail.length > 0) input.email = trimmedEmail;
      if (trimmedUserId.length > 0) input.member_user_id = trimmedUserId;
      if (workAnniversaryDate.length > 0)
        input.work_anniversary_date = workAnniversaryDate;
      if (trimmedNotes.length > 0) input.notes = trimmedNotes;
      return createFamilyMember(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["family-members"] });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save family member. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    if (name.trim().length === 0) {
      setInlineError("Name is required.");
      return;
    }
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit family member" : "Add family member"}
      className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {isEdit ? "Edit family member" : "Add family member"}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">Name</label>
          <input
            type="text"
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={mutation.isPending}
            placeholder="Christianna"
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Relationship</label>
          <select
            className={inputClass}
            value={relationship}
            onChange={(e) =>
              setRelationship(e.target.value as FamilyRelationship)
            }
            disabled={mutation.isPending}
          >
            {RELATIONSHIP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Date of birth <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="date"
            className={inputClass}
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Email <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="email"
            className={inputClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={mutation.isPending}
            placeholder="grandma@example.com"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Work anniversary{" "}
            <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="date"
            className={inputClass}
            value={workAnniversaryDate}
            onChange={(e) => setWorkAnniversaryDate(e.target.value)}
            disabled={mutation.isPending}
            aria-label="Work anniversary date"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium">
            Linked Hearth user ID{" "}
            <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="text"
            className={inputClass}
            value={memberUserId}
            onChange={(e) => setMemberUserId(e.target.value)}
            disabled={mutation.isPending}
            placeholder="ULID of a registered user (Andre/Christianna)"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium">
            Notes <span className="text-zinc-500">(optional)</span>
          </label>
          <textarea
            className={inputClass}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={mutation.isPending}
            maxLength={2000}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending
            ? "Saving…"
            : isEdit
              ? "Save member"
              : "Add member"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>

      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";
